import { Button } from "@/components/ui/button";
import { ArrowRight, CheckCircle, ChevronDown } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Link } from "react-router-dom";

const Hero = () => {
  const { t } = useLanguage();
  
  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-primary dark:bg-[#0a0a0a] pt-24 sm:pt-28" aria-labelledby="hero-heading">
      {/* Abstract flowing visual element */}
      <div className="absolute right-0 top-1/2 -translate-y-1/2 w-1/2 h-full pointer-events-none" aria-hidden="true">
        <svg viewBox="0 0 600 600" className="w-full h-full opacity-90" preserveAspectRatio="xMidYMid slice">
          <defs>
            <linearGradient id="flowGradient1" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#00d4aa" />
              <stop offset="25%" stopColor="#00b4d8" />
              <stop offset="50%" stopColor="#0077b6" />
              <stop offset="75%" stopColor="#7209b7" />
              <stop offset="100%" stopColor="#f72585" />
            </linearGradient>
          </defs>
          
          {/* Flowing abstract shape - ribbon style */}
          <g className="animate-[spin_60s_linear_infinite]" style={{ transformOrigin: 'center' }}>
            {[...Array(24)].map((_, i) => (
              <path
                key={i}
                d={`M300,100 
                   Q${400 + Math.sin(i * 0.5) * 50},${200 + Math.cos(i * 0.3) * 30} 
                   ${450 + Math.sin(i * 0.7) * 40},300 
                   Q${400 + Math.cos(i * 0.4) * 50},${400 + Math.sin(i * 0.6) * 30} 
                   300,500 
                   Q${200 - Math.sin(i * 0.5) * 50},${400 - Math.cos(i * 0.3) * 30} 
                   ${150 - Math.sin(i * 0.7) * 40},300 
                   Q${200 - Math.cos(i * 0.4) * 50},${200 - Math.sin(i * 0.6) * 30} 
                   300,100`}
                fill="none"
                stroke="url(#flowGradient1)"
                strokeWidth="2"
                opacity={0.3 + (i % 5) * 0.15}
                style={{
                  transform: `rotate(${i * 15}deg)`,
                  transformOrigin: 'center'
                }}
              />
            ))}
          </g>
          
          {/* Floating dots */}
          {[...Array(8)].map((_, i) => (
            <circle
              key={`dot-${i}`}
              cx={250 + Math.cos(i * 0.8) * 120}
              cy={250 + Math.sin(i * 0.8) * 120}
              r="6"
              fill="#ffffff"
              opacity="0.6"
              className="animate-pulse"
              style={{ animationDelay: `${i * 0.2}s` }}
            />
          ))}
        </svg>
      </div>

      {/* Subtle gradient overlay for depth */}
      <div className="absolute inset-0 bg-gradient-to-r from-primary via-primary/90 to-transparent dark:from-[#0a0a0a] dark:via-[#0a0a0a]/90" aria-hidden="true" />

      <div className="container relative z-10 mx-auto px-4 sm:px-6 md:px-8 lg:px-12">
        <div className="max-w-2xl">
          {/* LCP element - rendered with static content for fast paint */}
          <h1 
            id="hero-heading" 
            className="text-4xl md:text-5xl lg:text-6xl font-light mb-6 text-white leading-tight tracking-tight"
            style={{ contentVisibility: 'visible' }}
          >
            {t('hero.title')}
          </h1>
          
          <p className="text-lg md:text-xl text-white/90 mb-4 leading-relaxed font-medium">
            {t('hero.subtitle')}
          </p>
          
          <p className="text-base text-white/70 mb-10 leading-relaxed max-w-xl">
            Ontdek hoe wij jouw digitale toegankelijkheid naar het volgende niveau tillen met bewezen expertise en duurzame oplossingen.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4">
            <Link 
              to="/contact"
              className="inline-flex items-center justify-center bg-accent hover:bg-accent/90 text-accent-foreground shadow-lg hover:shadow-xl transition-all duration-300 text-base px-8 py-4 min-h-[44px] rounded-md font-medium"
              aria-label={t('hero.cta.primary')}
            >
              {t('hero.cta.primary')}
              <ArrowRight className="ml-2 w-5 h-5" aria-hidden="true" />
            </Link>
            <Link 
              to="/quickscan-aanvragen"
              className="inline-flex items-center justify-center border border-white/30 hover:border-white/50 bg-white/5 hover:bg-white/10 text-white transition-all duration-300 text-base px-8 py-4 min-h-[44px] rounded-md font-medium"
              aria-label={t('hero.cta.quickscan')}
            >
              {t('hero.cta.quickscan')}
            </Link>
          </div>

          <ul className="mt-16 flex flex-col sm:flex-row flex-wrap gap-6 text-white/80" aria-label="Belangrijkste voordelen">
            <li className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-accent" aria-hidden="true" />
              <span className="text-sm">{t('hero.benefit.1')}</span>
            </li>
            <li className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-accent" aria-hidden="true" />
              <span className="text-sm">{t('hero.benefit.2')}</span>
            </li>
            <li className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-accent" aria-hidden="true" />
              <span className="text-sm">{t('hero.benefit.3')}</span>
            </li>
          </ul>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10">
        <Button 
          size="lg" 
          variant="ghost" 
          className="text-white/50 hover:text-white/70 hover:bg-transparent"
          onClick={() => {
            const nextSection = document.querySelector('main section:nth-of-type(2)');
            nextSection?.scrollIntoView({ behavior: 'smooth' });
          }}
          aria-label={t('hero.cta.secondary')}
        >
          <ChevronDown className="w-6 h-6 animate-bounce" aria-hidden="true" />
        </Button>
      </div>
    </section>
  );
};

export default Hero;
